/**
 * Quantity Pricing Schedule editor (2026-08-28) — create AND edit in one
 * modal: identity fields, the 適用期間 apply period, and an INSERTABLE
 * bracket table.
 *
 * Research-bound design (bank
 * `.raw-research-2026-08-28-qtypricing-FABLE-LIVE`):
 * - Apply period as HEADER fields with an open-ended default, never
 *   per-bracket dates — the versioned unit is the whole bracket SET
 *   (`sap-scale-pricing-validity` #1/#2, C2 §3 ADAPT-conditional). SAP's
 *   31.12.9999 open end is modelled as an ABSENT side, never a fabricated
 *   far-future date.
 * - Bracket validators from the same finder (#6, marked APPLICABLE and D20
 *   input): thresholds strictly ascending, no duplicates, first bracket may
 *   start at 1, discounts 0–100.
 * - All-units (blanket) semantics and per-line scope are FIXED, not a
 *   per-schedule flag — cross-finder conflict A is routed to D20 (Sherl);
 *   until it rules, one hard-coded semantic keeps the pure resolver cheap.
 *   The caption states it (mandatory, C2 §4 UX-#4).
 * - NetSuite's 4-bracket cap is NOT enforced here: it is a D20 input, not a
 *   decided constant (C2 §1 #7).
 *
 * Saving is price-affecting ⇒ the reason gate (ReasonRequiredModal) is the
 * only path, exactly as the quick-create flow already does.
 */

import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Input, InputNumber, Modal, Table, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import type { QuantityPricingSchedule } from '@/types/api/pricing';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const scheduleFormSchema = z
  .object({
    nameZh: z.string().trim().min(1, 'pricing.quickCreate.validation.nameZhRequired'),
    nameEn: z.string().trim().min(1, 'pricing.quickCreate.validation.nameEnRequired'),
    code: z.string().trim().min(1, 'pricing.quickCreate.validation.codeRequired'),
    appliesFrom: z.string().nullable(),
    appliesTo: z.string().nullable(),
    brackets: z.array(
      z.object({
        minQuantity: z.number().int().min(1),
        discountPct: z.number().min(0).max(100),
      })
    ),
  })
  .superRefine((values, ctx) => {
    // Strictly ascending, duplicate-free thresholds (SAP finder #6).
    const seen = new Set<number>();
    values.brackets.forEach((bracket, index) => {
      if (seen.has(bracket.minQuantity)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['brackets', index, 'minQuantity'],
          message: 'pricing.scheduleEditor.validation.duplicateQuantity',
        });
      }
      seen.add(bracket.minQuantity);
    });
  });

export type ScheduleFormValues = z.infer<typeof scheduleFormSchema>;

export interface ScheduleEditorModalProps {
  open: boolean;
  /** Absent ⇒ create mode. */
  schedule?: QuantityPricingSchedule | null;
  /** Codes already taken — the edited schedule's own code is excluded by the host. */
  takenCodes: readonly string[];
  onCancel: () => void;
  /** Price-affecting ⇒ always carries the operator's reason. */
  onSave: (values: ScheduleFormValues, reason: string) => void;
}

const emptyValues: ScheduleFormValues = {
  nameZh: '',
  nameEn: '',
  code: '',
  appliesFrom: null,
  appliesTo: null,
  brackets: [],
};

export const ScheduleEditorModal = ({
  open,
  schedule,
  takenCodes,
  onCancel,
  onSave,
}: ScheduleEditorModalProps) => {
  const { t } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const [reasonOpen, setReasonOpen] = useState(false);
  const [pending, setPending] = useState<ScheduleFormValues | null>(null);

  const isEdit = !!schedule;

  const values = useMemo<ScheduleFormValues>(
    () =>
      schedule
        ? {
            nameZh: schedule.nameZh,
            nameEn: schedule.nameEn,
            code: schedule.code,
            appliesFrom: schedule.appliesFrom ?? null,
            appliesTo: schedule.appliesTo ?? null,
            brackets: schedule.brackets.map((bracket) => ({ ...bracket })),
          }
        : emptyValues,
    [schedule]
  );

  const {
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { errors },
  } = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: values,
    values,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const bracketArray = useFieldArray({ control, name: 'brackets' });

  // DA-8 — ≥100% discount is WARN-ONLY (SAP check-not-prohibition precedent;
  // Sherl may upgrade to block): the save stays allowed, the banner names the
  // zero-price consequence.
  const watchedBrackets = useWatch({ control, name: 'brackets' });
  const hasFullDiscount = (watchedBrackets ?? []).some(
    (bracket) => (bracket?.discountPct ?? 0) >= 100
  );

  useEffect(() => {
    if (open) reset(values);
  }, [open, values, reset]);

  const submit = (formValues: ScheduleFormValues) => {
    const code = formValues.code.trim();
    if (takenCodes.some((taken) => taken.toLowerCase() === code.toLowerCase())) {
      setError('code', { message: 'pricing.quickCreate.codeTaken' });
      return;
    }
    setPending(formValues);
    setReasonOpen(true);
  };

  const bracketColumns: TableProps<{ id: string }>['columns'] = [
    {
      title: t('pricing:pricing.scheduleEditor.columns.minQuantity'),
      key: 'minQuantity',
      width: 220,
      render: (_: unknown, __, index) => (
        <Controller
          name={`brackets.${index}.minQuantity`}
          control={control}
          render={({ field }) => (
            <InputNumber
              value={field.value}
              onChange={(next) => field.onChange(next ?? 1)}
              min={1}
              step={1}
              size="middle"
              // The FIRST bracket is pinned at 1 (synthesis §1.2D): it is the
              // concrete defence against SAP's confirmed to-scale trap, where
              // quantities below the lowest bracket fall through to no price.
              disabled={index === 0}
              status={errors.brackets?.[index]?.minQuantity ? 'error' : undefined}
              style={{ width: '100%' }}
            />
          )}
        />
      ),
    },
    {
      title: t('pricing:pricing.scheduleEditor.columns.discountPct'),
      key: 'discountPct',
      width: 220,
      render: (_: unknown, __, index) => (
        <Controller
          name={`brackets.${index}.discountPct`}
          control={control}
          render={({ field }) => (
            <InputNumber
              value={field.value}
              onChange={(next) => field.onChange(next ?? 0)}
              min={0}
              max={100}
              step={1}
              size="middle"
              addonAfter="%"
              style={{ width: '100%' }}
            />
          )}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_: unknown, __, index) => (
        <>
          {/* Insert-below on every row — the literal ask ("in the schedule
              should be able to insert brackets"). No drag-reorder: order is
              DERIVED from quantity, so dragging would imply an independent
              order that does not exist (synthesis §1.2D). */}
          <Button
            type="text"
            aria-label={t('pricing:pricing.scheduleEditor.insertBelow')}
            icon={<PlusOutlined />}
            onClick={() => {
              const current = getValues('brackets');
              const here = current[index]?.minQuantity ?? 1;
              const next = current[index + 1]?.minQuantity;
              // Midpoint when integral, else one step above (then the on-save
              // sort + duplicate check keeps the ladder honest).
              const seed =
                next !== undefined && next - here > 1
                  ? Math.floor((here + next) / 2)
                  : here + 10;
              bracketArray.insert(index + 1, { minQuantity: seed, discountPct: 0 });
            }}
          />
          <Button
            type="text"
            aria-label={t('pricing:pricing.scheduleEditor.removeBracket')}
            icon={<DeleteOutlined />}
            onClick={() => bracketArray.remove(index)}
          />
        </>
      ),
    },
  ];

  const duplicateMessage = errors.brackets
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (errors.brackets as any)?.find?.((entry: any) => entry?.minQuantity?.message)?.minQuantity
        ?.message
    : undefined;

  return (
    <>
      <Modal
        title={t(
          isEdit
            ? 'pricing:pricing.scheduleEditor.editTitle'
            : 'pricing:pricing.scheduleEditor.createTitle'
        )}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(submit)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        width={720}
        destroyOnHidden
        okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      >
        <div style={{ display: 'grid', gap: token.marginSM }}>
          {/* Explicit keys, never a template literal: a dynamic key is
              invisible to the i18n audit, which is how the first pass shipped
              raw `pricing.quickCreate.fields.nameZh` text into the modal. */}
          {(
            [
              ['nameZh', 'pricing:pricing.quickCreate.nameZh'],
              ['nameEn', 'pricing:pricing.quickCreate.nameEn'],
              ['code', 'pricing:pricing.quickCreate.code'],
            ] as const
          ).map(([name, labelKey]) => (
            <div key={name}>
              <Text style={formStyles.labelStyle}>{t(labelKey)}</Text>
              <Controller
                name={name}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    status={errors[name] ? 'error' : undefined}
                    style={formStyles.inputStyle}
                  />
                )}
              />
              {errors[name] && (
                <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
              )}
            </div>
          ))}

          <div>
            <Text style={formStyles.labelStyle}>
              {t('pricing:pricing.scheduleEditor.applyPeriod')}
            </Text>
            <Controller
              name="appliesFrom"
              control={control}
              render={({ field: fromField }) => (
                <Controller
                  name="appliesTo"
                  control={control}
                  render={({ field: toField }) => (
                    <RangePicker
                      // Either side may stay empty — an absent side is an OPEN
                      // end (SAP 31.12.9999 convention), never a fabricated date.
                      allowEmpty={[true, true]}
                      value={[
                        fromField.value ? dayjs(fromField.value) : null,
                        toField.value ? dayjs(toField.value) : null,
                      ]}
                      onChange={(range) => {
                        fromField.onChange(range?.[0] ? range[0].format('YYYY-MM-DD') : null);
                        toField.onChange(range?.[1] ? range[1].format('YYYY-MM-DD') : null);
                      }}
                      size="large"
                      style={{ width: '100%' }}
                    />
                  )}
                />
              )}
            />
            <Text
              type="secondary"
              style={{ display: 'block', marginTop: 4, fontSize: token.fontSizeSM }}
            >
              {t('pricing:pricing.scheduleEditor.applyPeriodHint')}
            </Text>
          </div>

          <div>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.scheduleEditor.bracketsTitle')}
              </Text>
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  // First bracket is pinned at 1 (to-scale trap defence);
                  // later ones seed one step above the current max.
                  const current = getValues('brackets');
                  const nextMin =
                    current.length === 0
                      ? 1
                      : Math.max(...current.map((bracket) => bracket.minQuantity)) + 10;
                  bracketArray.append({ minQuantity: nextMin, discountPct: 0 });
                }}
              >
                {t('pricing:pricing.scheduleEditor.addBracket')}
              </Button>
            </div>
            {hasFullDiscount && (
              <Alert
                type="warning"
                showIcon
                message={t('pricing:pricing.warnings.discountZeroPrice')}
                style={{ marginBottom: token.marginXS }}
              />
            )}
            <Table<{ id: string }>
              columns={bracketColumns}
              dataSource={bracketArray.fields as { id: string }[]}
              rowKey={(row) => row.id}
              size="small"
              pagination={false}
              locale={{
                emptyText: (
                  <Text type="secondary">
                    {t('pricing:pricing.scheduleEditor.noBrackets')}
                  </Text>
                ),
              }}
            />
            {duplicateMessage && (
              <Text style={formStyles.errorStyle}>{t(String(duplicateMessage))}</Text>
            )}
            <Text
              type="secondary"
              style={{ display: 'block', marginTop: 4, fontSize: token.fontSizeSM }}
            >
              {t('pricing:pricing.scheduleEditor.semanticsCaption')}
            </Text>
          </div>
        </div>
      </Modal>

      <ReasonRequiredModal
        open={reasonOpen}
        title={t('pricing:pricing.schedulesAdmin.reason.title')}
        description={t('pricing:pricing.schedulesAdmin.reason.description')}
        reasonLabel={t('pricing:pricing.schedulesAdmin.reason.label')}
        reasonPlaceholder={t('pricing:pricing.schedulesAdmin.reason.placeholder')}
        requiredMessage={t('pricing:pricing.schedulesAdmin.reason.required')}
        submitText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        onSubmit={(reason) => {
          setReasonOpen(false);
          if (pending) onSave(pending, reason);
          setPending(null);
        }}
        onCancel={() => setReasonOpen(false)}
      />
    </>
  );
};

export default ScheduleEditorModal;
